/**
 * OCT20A WS-ROLL2 (R5) — the three continuation situations, in people's words. Pure; no I/O.
 *
 * The internal reason stays `intentionally_continued`. The server classifies (continuation_provenance):
 *   planned_multiday     → "Planned Multiday Repair — Continuation"   (booked across days; D-3 booking approval)
 *   authorized_rollover  → "Authorized Rollover — Continuation"       (carried after a NEW human approval)
 *   pending overrun      → "Projected EOD Overrun — Awaiting Approval" (never shown as authorized)
 * An unapproved overrun is never relabelled as an authorized continuation: `pending` is a separate line and never
 * changes the category.
 */
import { formatEodOverrun } from './scheduleAlerts';

export type ContinuationCategory = 'planned_multiday' | 'authorized_rollover' | 'unclassified' | 'not_a_continuation';

export const CONTINUATION_LABEL: Record<'planned_multiday' | 'authorized_rollover', string> = {
  planned_multiday: 'Planned Multiday Repair — Continuation',
  authorized_rollover: 'Authorized Rollover — Continuation',
};
export const OVERRUN_AWAITING_APPROVAL = 'Projected EOD Overrun — Awaiting Approval';

export interface ContinuationProvenance {
  outcome?: string;
  category?: ContinuationCategory | string;
  approved_by_name?: string | null;
  approved_at?: string | null;
  approved_scope?: { approved_last_date?: string | null; approved_days?: number | null; approved_work_min?: number | null } | null;
  carry_reason_note?: string | null;
  pending?: { kind?: string; overrun_min?: number | null } | null;
}

export interface ContinuationLines {
  label: string | null;
  approval: string | null;
  scope: string | null;
  pending: string | null;
}

export function describeContinuationProvenance(p: ContinuationProvenance | null | undefined,
  formatWhen: (iso: string) => string = (iso) => iso): ContinuationLines {
  if (!p || p.outcome !== 'OK') return { label: null, approval: null, scope: null, pending: null };
  const label = p.category === 'planned_multiday' || p.category === 'authorized_rollover' ? CONTINUATION_LABEL[p.category] : null;
  let approval: string | null = null;
  if (label && (p.approved_by_name || p.approved_at)) {
    const verb = p.category === 'planned_multiday' ? 'Planned' : 'Approved';
    approval = `${verb}${p.approved_by_name ? ` by ${p.approved_by_name}` : ''}${p.approved_at ? ` · ${formatWhen(p.approved_at)}` : ''}`;
  }
  const sc = p.approved_scope;
  const scope = label && sc && (sc.approved_last_date || sc.approved_days)
    ? `Approved scope: ${sc.approved_days ? `${sc.approved_days} day${sc.approved_days === 1 ? '' : 's'}` : ''}${sc.approved_days && sc.approved_last_date ? ', ' : ''}${sc.approved_last_date ? `through ${sc.approved_last_date}` : ''}`
    : null;
  let pending: string | null = null;
  if (p.pending?.kind === 'overrun_awaiting_approval') {
    pending = p.pending.overrun_min ? `${formatEodOverrun(p.pending.overrun_min)} — Awaiting Approval` : OVERRUN_AWAITING_APPROVAL;
  } else if (p.pending?.kind === 'scheduling_decision_required') {
    pending = 'Scheduling Decision Required';
  }
  return { label, approval, scope, pending };
}
